import React, { useCallback, useEffect, useState, startTransition } from "react";
import { useNavigate } from "react-router-dom";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Button,
  InlineStack,
  BlockStack,
  Badge,
  EmptyState,
  Banner,
  Modal,
  Box,
  SkeletonBodyText,
} from "@shopify/polaris";
import { EditIcon, DeleteIcon } from "@shopify/polaris-icons";
import { fetchTemplates, deleteTemplate } from "../../api/templatesApi";
import { countryLabel, languageLabel } from "../../settings/countryLanguageOptions";
import { useAppTitleBar } from "../../hooks/useAppTitleBar";

export default function SettingsListPage() {
  const rrNavigate = useNavigate();
  const navigate = useCallback(
    (to, options) => {
      startTransition(() => {
        rrNavigate(to, options);
      });
    },
    [rrNavigate],
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [rows, setRows] = useState([]);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null, name: "" });
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  const load = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    setError(null);
    if (!silent) setLoading(true);
    try {
      const res = await fetchTemplates();
      const list = res?.data?.templates ?? [];
      setRows(Array.isArray(list) ? list : []);
    } catch (e) {
      const msg =
        typeof e === "string" ? e : e instanceof Error ? e.message : e?.message || "Failed to load templates";
      setError(msg);
      setRows([]);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const onCreate = useCallback(() => {
    setError(null);
    navigate("/settings/templates/new");
  }, [navigate]);

  const openDeleteModal = useCallback((row, templateCount) => {
    if (row.is_primary && templateCount > 1) return;
    setDeleteModal({ open: true, id: row.id, name: row.name || "Template" });
  }, []);

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ open: false, id: null, name: "" });
  }, []);

  const confirmDelete = useCallback(async () => {
    const id = deleteModal.id;
    if (id == null) return;
    setDeleteSubmitting(true);
    setError(null);
    try {
      await deleteTemplate(id);
      closeDeleteModal();
      await load({ silent: true });
    } catch (e) {
      const msg =
        typeof e === "string" ? e : e instanceof Error ? e.message : e?.message || "Delete failed";
      setError(msg);
    } finally {
      setDeleteSubmitting(false);
    }
  }, [deleteModal.id, load, closeDeleteModal]);

  useAppTitleBar({
    title: "Settings",
    primaryAction: { label: "Create template", onClick: onCreate },
  });

  return (
    <Page>
      <BlockStack gap="500">
        {error ? (
          <Banner
            tone="critical"
            title="Something went wrong"
            action={{ content: "Try again", onAction: () => void load() }}
            onDismiss={() => setError(null)}
          >
            <p>{error}</p>
          </Banner>
        ) : null}

        <Card padding="0" roundedAbove="md">
            {loading ? (
              <Box padding="500" minHeight="320px">
                <BlockStack gap="400">
                  <SkeletonBodyText lines={2} />
                  <SkeletonBodyText lines={6} />
                </BlockStack>
              </Box>
            ) : rows.length === 0 ? (
              <Box paddingBlockStart="400" paddingBlockEnd="600">
                <EmptyState
                  heading="No templates yet"
                  action={{ content: "Create template", onAction: onCreate }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Create a template to configure popup and checkout verification for your storefront and checkout.
                  </p>
                </EmptyState>
              </Box>
            ) : (
              <div className="age-settings-templates-index-table">
              <IndexTable
                resourceName={{ singular: "template", plural: "templates" }}
                itemCount={rows.length}
                headings={[
                  { title: "Template" },
                  { title: "Country" },
                  { title: "Language" },
                  { title: "Popup" },
                  { title: "Checkout" },
                  { title: "Actions", alignment: "end", id: "templates-actions-heading" },
                ]}
                selectable={false}
                hasZebraStriping={false}
                lastColumnSticky
              >
                {rows.map((row, index) => {
                  const popupOn = !!row.popup_enabled;
                  const checkoutOn = !!row.checkout_enabled;

                  return (
                    <IndexTable.Row id={String(row.id)} key={row.id} position={index}>
                      <IndexTable.Cell>
                        <BlockStack gap="100">
                          <InlineStack gap="200" blockAlign="center" wrap={false}>
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              {row.name}
                            </Text>
                            {row.is_primary ? (
                              <Badge tone="info" size="small">
                                Primary
                              </Badge>
                            ) : null}
                          </InlineStack>
                        </BlockStack>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text as="span" variant="bodyMd">
                          {countryLabel(row.country_code)}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Text as="span" variant="bodyMd">
                          {languageLabel(row.language_code)}
                        </Text>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={popupOn ? "success" : "info"} size="small">
                          {popupOn ? "On" : "Off"}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Badge tone={checkoutOn ? "success" : "info"} size="small">
                          {checkoutOn ? "On" : "Off"}
                        </Badge>
                      </IndexTable.Cell>
                      <IndexTable.Cell>
                        <Box maxWidth="100%" paddingInlineEnd="200">
                          <InlineStack gap="200" align="end" blockAlign="center" wrap={false}>
                            <Button
                              variant="plain"
                              icon={EditIcon}
                              accessibilityLabel={`Edit ${row.name || "template"}`}
                              onClick={() => navigate(`/settings/templates/${row.id}`)}
                              className="age-settings-templates-table__iconBtn age-settings-templates-table__iconBtn--edit"
                            />
                            <Button
                              variant="plain"
                              icon={DeleteIcon}
                              accessibilityLabel={`Delete ${row.name || "template"}`}
                              disabled={!!row.is_primary && rows.length > 1}
                              onClick={() => openDeleteModal(row, rows.length)}
                              className="age-settings-templates-table__iconBtn age-settings-templates-table__iconBtn--delete"
                            />
                          </InlineStack>
                        </Box>
                      </IndexTable.Cell>
                    </IndexTable.Row>
                  );
                })}
              </IndexTable>
              </div>
            )}
        </Card>
      </BlockStack>

      <Modal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        title="Delete template?"
        primaryAction={{
          content: "Delete template",
          destructive: true,
          loading: deleteSubmitting,
          onAction: () => {
            void confirmDelete();
          },
        }}
        secondaryActions={[
          {
            content: "Cancel",
            disabled: deleteSubmitting,
            onAction: closeDeleteModal,
          },
        ]}
      >
        <Modal.Section>
          <Text as="p" variant="bodyMd">
            Delete <strong>{deleteModal.name}</strong>? This cannot be undone.
          </Text>
        </Modal.Section>
      </Modal>
    </Page>
  );
}
